'use client';

import React, { useEffect, useRef } from 'react';

import Link from 'next/link';

import {
  selectDomain,
  selectIsTenant,
  useDomainStore,
} from '@/providers/domain-store-provider';
import {
  selectMobile,
  selectOTPExpiresIn,
  selectResetLogin,
  selectSetLoginToken,
  selectSetMobile,
  useAuthStore,
} from '@/store/use-auth.store';
import { Typography } from 'antd';
import { Formik, Form as FormikForm } from 'formik';
import type { FormikProps } from 'formik';
import * as Yup from 'yup';

import {
  type RegisterMutationParams,
  useRegister,
} from '@/common/apis/identity/v1/register.api';
import AnalyticsWrapper from '@/common/components/analytics-wrapper';
import Button from '@/common/components/button';
import { FormikNumericInput } from '@/common/components/input';
import Toast from '@/common/components/toast/toast.component';
import { appConfigs } from '@/common/configs/app.config';
import { regex } from '@/common/constants/regex.constant';

const { Text } = Typography;

const validationSchema = Yup.object().shape({
  username: Yup.string()
    .required('شماره موبایل خود را وارد کنید.')
    .min(11, 'شماره وارد شده باید 11 رقم باشد.')
    .matches(regex.mobileNumberRegex, 'شماره تلفن وارد شده معتبر نیست'),
});

interface LoginFormProps {
  showOTP: () => void;
}

export default function LoginForm({ showOTP }: LoginFormProps) {
  const resetLogin = useAuthStore(selectResetLogin);
  const setMobile = useAuthStore(selectSetMobile);
  const setLoginToken = useAuthStore(selectSetLoginToken);
  const otpExpiresIn = useAuthStore(selectOTPExpiresIn);
  const mobile = useAuthStore(selectMobile);

  const { mutate: register, isSuccess, isPending, data } = useRegister();

  const formikRef = useRef<FormikProps<RegisterMutationParams>>(null);

  const config = useDomainStore(selectDomain);
  const isTenant = useDomainStore(selectIsTenant);
  const isPrivacyPageActive = config?.pagesStatus.contentPrivacyAndPolicy;
  const tenantHasPrivacyPage = isTenant && isPrivacyPageActive;
  const showPrivayPage = !isTenant || tenantHasPrivacyPage;

  const errorHandler = () => {
    const { current } = formikRef;
    if (current?.errors.username && current?.touched.username) {
      Toast.error(current?.errors.username);
    }
  };

  const onSubmitHandler = async (values: RegisterMutationParams) => {
    if (values.username !== mobile || otpExpiresIn <= Date.now()) {
      register(values);
      resetLogin();
    } else {
      showOTP();
    }
  };

  useEffect(() => {
    if (isSuccess) {
      if (data?.data.token) {
        setMobile(data.data.phoneNumber);
        setLoginToken(data.data.token);
      }
      showOTP();
    }
  }, [isPending, isSuccess]);

  return (
    <div className="h-full w-full">
      <Formik
        initialValues={{
          username: mobile || '',
        }}
        validationSchema={validationSchema}
        onSubmit={onSubmitHandler}
        innerRef={formikRef}
        enableReinitialize
      >
        {({ errors }) => (
          <FormikForm autoComplete="off">
            <Text className="mt-2 block text-base font-medium text-[#191308]">
              شماره موبایل تون رو وارد کنین.
            </Text>
            <FormikNumericInput
              maxLength={11}
              id="username"
              label="شماره تلفن همراه"
              name="username"
              type="tel"
              error={errors.username}
              className="w-full text-center"
            />
            {!isTenant && (
              <div className="mb-[32px]  flex w-full items-center justify-between">
                <Text className="text-xs font-medium text-black-200">
                  میخواین فروشگاه بسازین؟
                </Text>

                <a
                  href={`${appConfigs.witroAppUrl}`}
                  className="text-xs font-medium text-blue-default"
                  target="_blank"
                  rel="noreferrer"
                >
                  ورود به عنوان فروشنده
                </a>
              </div>
            )}

            <AnalyticsWrapper analytics={['auth-submit-phone-v1']}>
              <Button
                type="black"
                className="!mt-0 w-full"
                htmlType="submit"
                onClick={errorHandler}
                loading={isPending}
              >
                دریافت کد تایید
              </Button>
            </AnalyticsWrapper>
            {showPrivayPage && (
              <div className="mt-4 flex w-full items-center justify-center ">
                <Text className="text-center text-sm font-medium text-black-300">
                  ورود‌تون به معنای پذیرش{' '}
                  <span>
                    <Link
                      target="_blank"
                      href={isTenant ? '/privacy-policies' : '/privacy-policy'}
                      style={{ color: '#0199FE' }}
                      className="cursor-pointer"
                    >
                      قوانین و مقررات
                    </Link>
                  </span>{' '}
                  هست.
                </Text>
              </div>
            )}
          </FormikForm>
        )}
      </Formik>
    </div>
  );
}
